// Marketing site entrypoint (every marketing page). The pages are server-rendered and fully
// readable without JavaScript; this adds the motion ported from the approved prototype.
// With `prefers-reduced-motion: reduce` there is no smooth scroll, no WebGL and no
// entrance: only the functional bits (FAQ accordion, map control, cobranzas tabs) run.
import '../marketing/styles.css'
import { wireAuth } from '../marketing/auth'
import { CHAPTERS, pickQuoteWinner } from '../marketing/chapters'
import { type Constellation, createConstellation, type GlParams } from '../marketing/constellation'
import { wireHero } from '../marketing/hero'
import { createLenis } from '../marketing/lenis'
import { wireMap } from '../marketing/map'
import {
  $, $$, ST, G, motion, prefersReducedMotion, wireBand, wireBento, wireCursor, wireFaq, wireFinal, wireGrain,
  wireHeadings, wireManifesto, wireNav, wireProgress, wireProof,
} from '../marketing/misc'
import { wirePortal } from '../marketing/portal'

declare global {
  interface Window { __adminseMarketing?: { readonly gl: Constellation | null } }
}

const parseGl = (el: HTMLElement): GlParams | null => {
  try { return JSON.parse(el.dataset.gl || '') as GlParams } catch { return null }
}

/** Each chapter's `data-gl` sends the constellation to its shape as the chapter arrives. */
function wireGlChapters(): void {
  $$('[data-gl]').forEach((sec) => {
    const p = parseGl(sec)
    if (!p) return
    const go = () => { current = p; motion.gl?.go(p) }
    ST.create({ trigger: sec, start: 'top 62%', end: 'bottom 38%', onEnter: go, onEnterBack: go })
  })
}
let current: GlParams | null = null

/** Runs one module's wiring; a missing hook or a throw is reported and the rest still boots. */
const safely = <T>(name: string, fn: () => T): T | undefined => {
  try {
    return fn()
  } catch (e) {
    console.warn(`[marketing] ${name} failed to wire`, e)
    return undefined
  }
}

/** If the hero entrance could not run, put back everything it had hidden. */
const revealHero = () => {
  try {
    G.killTweensOf('.hero *')
    G.set('.hero .pill, .hero .kick, .hero .rot, .hero .lead, .hero .ctas > *, .hero-stats li, .hero .h-hero .wi, .hero .h-hero .ch', { clearProps: 'opacity,transform,filter,visibility' })
  } catch { /* GSAP itself is broken: the hero was never hidden either. */ }
}

/** Hands the main thread back to the browser so input and paint are not held behind boot. */
const yieldToMain = (): Promise<void> => new Promise((resolve) => { setTimeout(resolve, 0) })

export async function boot(): Promise<void> {
  // Debug handle only: the live constellation, null until three.js has loaded.
  window.__adminseMarketing = { get gl() { return motion.gl } }
  const animated = !prefersReducedMotion()
  // Auth forms behave the same with or without motion.
  safely('auth', wireAuth)

  if (!animated) {
    // No entrance ever runs: `html.rm` makes the CSS show every demo's final, still frame
    // and stack the cobranzas stops (see styles.css).
    document.documentElement.classList.add('rm')
    safely('nav', () => wireNav(null))
    safely('faq', () => wireFaq(false))
    safely('map', () => wireMap(false))
    safely('portal', () => wirePortal(false))
    safely('plata', unwirePlataTabs)
    return
  }

  G.registerPlugin(ST)
  motion.lenis = safely('lenis', createLenis) ?? null
  safely('grain', wireGrain)
  safely('nav', () => wireNav(motion.lenis))
  safely('cursor', wireCursor)
  safely('portal', () => wirePortal(true))
  safely('progress', wireProgress)

  // The hero is the first thing on screen and its headline is the page's largest paint, so its
  // entrance starts before the rest of the page is wired. The hero feed quotes the quoter's
  // winner, so that pick runs first.
  safely('quote winner', pickQuoteWinner)
  const heroIn = safely('hero', wireHero)
  const hero = $('.hero')
  current = hero ? parseGl(hero) : null
  if (heroIn) { if (safely('hero entrance', () => { heroIn(); return true }) === undefined) revealHero() } else if (hero) revealHero()

  // Everything below the fold is wired one module per task, in page order.
  const rest: (readonly [string, () => unknown])[] = [
    ...CHAPTERS.map(([name, wire]) => [`chapter ${name}`, wire] as const),
    ['band', wireBand], ['headings', wireHeadings], ['manifesto', wireManifesto], ['map', () => wireMap(true)],
    ['bento', wireBento], ['proof', wireProof], ['faq', () => wireFaq(true)], ['final', wireFinal],
    ['gl chapters', wireGlChapters],
  ]
  for (const [name, wire] of rest) {
    await yieldToMain()
    safely(name, wire)
  }

  const canvas = $<HTMLCanvasElement>('#gl')
  if (canvas) {
    void createConstellation(canvas).then((gl) => {
      if (!gl) return
      motion.gl = gl
      // The cloud arrives late (idle + lazy chunk): send it to wherever the visitor is now.
      if (current) gl.go(current)
    }).catch((e) => console.warn('[marketing] constellation failed', e))
  }

  const refresh = () => ST.refresh()
  if (document.fonts) {
    void document.fonts.ready.then(refresh)
    // The web font stylesheet switches on without blocking, possibly after `ready` resolved:
    // measure the chapters again whenever a font finishes loading.
    let pending = 0
    document.fonts.addEventListener('loadingdone', () => { clearTimeout(pending); pending = window.setTimeout(refresh, 150) })
  } else refresh()
}

/**
 * Reduced motion: the cobranzas story does not play and its four stops are stacked
 * panels, each with its own heading. A tablist would hide three of them behind
 * selection that means nothing here, so the tabs are hidden (CSS) and the panels lose
 * their tab roles: keyboard and screen-reader users read the four stops in order.
 */
function unwirePlataTabs(): void {
  $$('#plata [role="tabpanel"]').forEach((p) => { p.removeAttribute('role'); p.removeAttribute('aria-labelledby') })
}

void boot()
